import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { selectUserId, selectTeamIds, selectTeams, selectTeamName, selectTier, selectSportsClubName, selectSportsClubId } from '../store/userSlice';
import { useAuth } from '../context/AuthContext';
import { PreseasonGameControllerService, TeamControllerService, TermControllerService } from '../api';
import { toast } from 'react-toastify';
import './SportsClubMatches.css';

function SportsClubMatches() {
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeamId, setSelectedTeamId] = useState('all');
  const [selectedScheduleId, setSelectedScheduleId] = useState('all');
  const [availableDateRanges, setAvailableDateRanges] = useState([]);
  const [isLoadingDateRanges, setIsLoadingDateRanges] = useState(false);
  const pendingScheduleIdRef = useRef(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const userId = useSelector(selectUserId);
  const teamIds = useSelector(selectTeamIds);
  const teamId = teamIds?.[0];
  const reduxTeams = useSelector(selectTeams);
  const teamName = useSelector(selectTeamName);
  const tier = useSelector(selectTier);
  const sportsClubName = useSelector(selectSportsClubName);
  const sportsClubId = useSelector(selectSportsClubId);
  const { hasRole } = useAuth();
  const isCoach = hasRole('COACH');
  const isSportsClub = hasRole('SPORTS_CLUB');
  const navigate = useNavigate();
  const location = useLocation();

  // Odczytaj parametry z przekierowania (z /sparingi lub /preseason-games-summary)
  useEffect(() => {
    // Jeśli przekazano teamId, ustaw go jako wybrany
    if (location.state?.teamId) {
      setSelectedTeamId(location.state.teamId.toString());
    }
    // Zapisz pending scheduleId do ustawienia po załadowaniu zakresów dat
    if (location.state?.termId) {
      pendingScheduleIdRef.current = location.state.termId;
    }
    // Wyczyść state po odczytaniu
    if (location.state?.teamId || location.state?.termId) {
      navigate('/sports-club-matches', { replace: true, state: {} });
    }
  }, [location.state, navigate, isSportsClub]);

  const getStatusClass = (status) => {
    const statusClasses = {
      'PENDING': 'status-oczekuje',
      'CONFIRMED': 'status-zaakceptowano',
      'BLOCKED': 'status-odrzucono'
    };
    return statusClasses[status] || 'status-nieznany';
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      'PENDING': 'Oczekuje',
      'CONFIRMED': 'Potwierdzono',
      'BLOCKED': 'Odrzucono'
    };
    return statusLabels[status] || status;
  };

  // Fetch teams
  useEffect(() => {
    const fetchTeams = async () => {
      if (isCoach) {
        // Dla COACH: mapuj drużyny z Redux do lokalnego formatu
        const mappedTeams = (reduxTeams || []).map(t => ({ teamId: t.id, name: t.teamName }));
        setTeams(mappedTeams);
        // Ustaw pierwszą drużynę jeśli nie wybrano jeszcze konkretnej
        if (selectedTeamId === 'all' && mappedTeams.length > 0) {
          setSelectedTeamId(mappedTeams[0].teamId.toString());
        }
        return;
      }

      if (!sportsClubId) return;

      try {
        const teamsData = await TeamControllerService.getTeamsBySportsClubId(sportsClubId);
        setTeams(teamsData || []);
      } catch (error) {
        // error handled silently
      }
    };

    fetchTeams();
  }, [sportsClubId, isCoach, reduxTeams]);

  // Pobierz zakresy dat dla wybranej drużyny
  useEffect(() => {
    const fetchDateRanges = async (teamIdToFetch) => {
      setIsLoadingDateRanges(true);
      try {
        const dateRanges = await TermControllerService.getAllDatesForSchedule(
          Number(teamIdToFetch)
        );
        setAvailableDateRanges(dateRanges || []);
      } catch (error) {
        setAvailableDateRanges([]);
      } finally {
        setIsLoadingDateRanges(false);
      }
    };

    // Pobierz zakresy dat dla wybranej drużyny (nie dla 'all')
    if (selectedTeamId && selectedTeamId !== 'all') {
      fetchDateRanges(selectedTeamId);
    }
    // Dla SPORTS_CLUB z 'all' - wyczyść zakresy
    else if (isSportsClub && selectedTeamId === 'all') {
      setAvailableDateRanges([]);
      setSelectedScheduleId('all');
    }
  }, [isCoach, isSportsClub, teamId, selectedTeamId]);

  // Ustaw wybrany zakres dat gdy availableDateRanges są załadowane i jest pendingScheduleId
  useEffect(() => {
    if (pendingScheduleIdRef.current && availableDateRanges.length > 0) {
      const pendingId = Number(pendingScheduleIdRef.current);
      const found = availableDateRanges.some(r => r.id === pendingId);
      if (found) {
        setSelectedScheduleId(pendingId.toString());
      }
      pendingScheduleIdRef.current = null;
    }
  }, [availableDateRanges]);

  // Reset page when selectedTeamId changes
  useEffect(() => {
    setPage(0);
  }, [selectedTeamId]);

  // Fetch matches when page or selectedTeamId changes
  useEffect(() => {
    const fetchMatches = async () => {
      // Sprawdź czy mamy wybraną drużynę
      if ((isCoach && (!selectedTeamId || selectedTeamId === 'all')) || (isSportsClub && !userId)) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const pageable = { page, size: 10 };
        let response;

        if (isCoach) {
          // Dla COACH pobierz oczekujące sparingi dla wybranej drużyny
          response = await PreseasonGameControllerService.getPendingPreseasonGamesByTeamCreator(parseInt(selectedTeamId), pageable);
        } else if (isSportsClub) {
          if (selectedTeamId === 'all') {
            // Pobierz wszystkie sparingi dla klubu sportowego
            response = await PreseasonGameControllerService.getPendingPreseasonGamesBySportsClubCreator(userId, pageable);
          } else {
            // Pobierz oczekujące sparingi dla konkretnej drużyny
            response = await PreseasonGameControllerService.getPendingPreseasonGamesByTeamCreator(parseInt(selectedTeamId), pageable);
          }
        }

        setMatches(response?.content || []);
        setTotalPages(response?.totalPages || 0);
        setTotalElements(response?.totalElements || 0);
      } catch (error) {
        toast.error('Nie udało się pobrać zapytań');
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, [userId, teamId, page, selectedTeamId, isCoach, isSportsClub]);

  const openModal = (match, e) => {
    e.stopPropagation();
    setSelectedMatch(match);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedMatch(null);
    setReplyMessage('');
  };

  const handleSendReply = async () => {
    if (!selectedMatch || !replyMessage.trim()) return;

    try {
      setSendingReply(true);

      // Określ senderId i receiverId w zależności od kontekstu
      // W SportsClubMatches - zalogowany użytkownik jest creatorem
      const senderId = selectedMatch.teamCreatorId;
      const receiverId = selectedMatch.teamOpponentId;

      await PreseasonGameControllerService.addMessage({
        preseasonGameId: selectedMatch.id,
        senderId: senderId,
        receiverId: receiverId,
        senderUserId: userId,
        message: replyMessage.trim()
      });

      toast.success('Wiadomość została wysłana');
      setReplyMessage('');

      // Odśwież dane matcha, aby pokazać nową wiadomość
      const pageable = { page, size: 10 };
      let response;

      if (isCoach) {
        response = await PreseasonGameControllerService.getPendingPreseasonGamesByTeamCreator(teamId, pageable);
      } else if (isSportsClub) {
        if (selectedTeamId === 'all') {
          response = await PreseasonGameControllerService.getPendingPreseasonGamesBySportsClubCreator(userId, pageable);
        } else {
          response = await PreseasonGameControllerService.getPreseasonGamesByTeamCreator(parseInt(selectedTeamId), pageable);
        }
      }

      setMatches(response?.content || []);

      // Zaktualizuj selectedMatch z nowymi danymi
      const updatedMatch = response?.content?.find(m => m.id === selectedMatch.id);
      if (updatedMatch) {
        setSelectedMatch(updatedMatch);
      }
    } catch (error) {
      toast.error('Nie udało się wysłać wiadomości');
    } finally {
      setSendingReply(false);
    }
  };

  const handleConfirmTime = async (match, e) => {
    e.stopPropagation();
    try {
      // Format time to HH:mm
      const formatTime = (time) => time ? time.substring(0, 5) : null;

      await PreseasonGameControllerService.approvePreseasonGame(match.id, match.teamCreatorId);
      toast.success('Godzina meczu została potwierdzona');

      // Odśwież dane
      const pageable = { page, size: 10 };
      let response;

      if (isCoach) {
        response = await PreseasonGameControllerService.getPendingPreseasonGamesByTeamCreator(teamId, pageable);
      } else if (isSportsClub) {
        if (selectedTeamId === 'all') {
          response = await PreseasonGameControllerService.getPendingPreseasonGamesBySportsClubCreator(userId, pageable);
        } else {
          response = await PreseasonGameControllerService.getPreseasonGamesByTeamCreator(parseInt(selectedTeamId), pageable);
        }
      }

      setMatches(response?.content || []);
      setTotalPages(response?.totalPages || 0);
      setTotalElements(response?.totalElements || 0);
    } catch (error) {
      if (error?.body?.error === 'SportsClubNotApproved') {
        toast.error(error.body.message);
      } else {
        toast.error('Nie udało się potwierdzić godziny');
      }
    }
  };

  // Pobierz zakres dat dla wybranego scheduleId
  const getSelectedDateRange = () => {
    if (selectedScheduleId === 'all' || !availableDateRanges.length) {
      return null;
    }
    return availableDateRanges.find(r => r.id?.toString() === selectedScheduleId);
  };

  const selectedDateRange = getSelectedDateRange();

  // Filtruj matches po wybranym zakresie dat i ukryj anulowane
  const filteredMatches = matches.filter(match => {
    // Ukryj anulowane wydarzenia
    if (match.status === 'CANCELLED') return false;
    if (!selectedDateRange) return true; // Jeśli brak wybranego zakresu, pokazuj wszystkie
    if (!match.date) {
      // Mecz bez daty — filtruj po schedule creatora
      const scheduleStart = match.creatorScheduleStartDate;
      const scheduleEnd = match.creatorScheduleEndDate;
      if (!scheduleStart && !scheduleEnd) return true; // brak info o schedule — pokazuj
      if (selectedDateRange.startDate && scheduleEnd && scheduleEnd < selectedDateRange.startDate) return false;
      if (selectedDateRange.endDate && scheduleStart && scheduleStart > selectedDateRange.endDate) return false;
      return true;
    }
    const matchDate = match.date;
    if (selectedDateRange.startDate && matchDate < selectedDateRange.startDate) return false;
    if (selectedDateRange.endDate && matchDate > selectedDateRange.endDate) return false;
    return true;
  });

  // Grupuj matche według drużyny gdy wybrano 'all'
  const groupedMatches = () => {
    if (selectedTeamId === 'all') {
      const groups = {};
      filteredMatches.forEach(match => {
        const teamName = match.teamCreatorName || `Drużyna #${match.teamCreatorId}`;
        if (!groups[teamName]) {
          groups[teamName] = [];
        }
        groups[teamName].push(match);
      });
      return groups;
    }
    return null;
  };

  const matchGroups = groupedMatches();

  if (loading) {
    return (
      <div className="sports-club-matches-container">
        <div className="loading">Ładowanie zapytań...</div>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="sports-club-matches-container">
        <div className="no-matches">Musisz być zalogowany, aby zobaczyć swoje zapytania.</div>
      </div>
    );
  }

  return (
    <div className="sports-club-matches-container">
      <h2>Twoje zapytania</h2>

      {/* Mobile: dropdown drużyny */}
      {teams.length > 0 && (
        <div className="mobile-team-select-wrapper">
          <select
            className="mobile-team-select"
            value={selectedTeamId}
            onChange={(e) => setSelectedTeamId(e.target.value)}
          >
            {isSportsClub && <option value="all">Wszystkie drużyny</option>}
            {teams.map(team => (
              <option key={team.teamId} value={team.teamId}>
                {team.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Mobile: nawigator zakresów dat */}
      <div className="mobile-date-nav">
        <button
          className="mobile-date-nav-arrow"
          disabled={selectedTeamId === 'all' || availableDateRanges.length === 0 || selectedScheduleId === 'all'}
          onClick={() => {
            const idx = availableDateRanges.findIndex(r => r.id.toString() === selectedScheduleId.toString());
            if (idx <= 0) {
              setSelectedScheduleId('all');
            } else {
              setSelectedScheduleId(availableDateRanges[idx - 1].id.toString());
            }
          }}
        >
          &#8592;
        </button>
        <span className="mobile-date-nav-label">
          {selectedTeamId === 'all' || isLoadingDateRanges
            ? 'Wszystkie daty'
            : selectedScheduleId === 'all'
              ? 'Wszystkie daty'
              : (() => {
                  const range = availableDateRanges.find(r => r.id.toString() === selectedScheduleId.toString());
                  return range ? `${range.startDate} - ${range.endDate}` : 'Wszystkie daty';
                })()
          }
        </span>
        <button
          className="mobile-date-nav-arrow"
          disabled={selectedTeamId === 'all' || availableDateRanges.length === 0 || (selectedScheduleId !== 'all' && availableDateRanges.findIndex(r => r.id.toString() === selectedScheduleId.toString()) >= availableDateRanges.length - 1)}
          onClick={() => {
            if (selectedScheduleId === 'all') {
              if (availableDateRanges.length > 0) setSelectedScheduleId(availableDateRanges[0].id.toString());
            } else {
              const idx = availableDateRanges.findIndex(r => r.id.toString() === selectedScheduleId.toString());
              if (idx < availableDateRanges.length - 1) setSelectedScheduleId(availableDateRanges[idx + 1].id.toString());
            }
          }}
        >
          &#8594;
        </button>
      </div>

      <div className="mobile-filters-divider"></div>

      {/* Desktop: dropdown drużyny + nawigator dat */}
      <div className="desktop-filters">
        {teams.length > 0 && (
          <div className="desktop-team-select-wrapper">
            <select
              className="desktop-team-select"
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
            >
              {isSportsClub && <option value="all">Wszystkie drużyny</option>}
              {teams.map(team => (
                <option key={team.teamId} value={team.teamId}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="desktop-date-nav">
          <button
            className="desktop-date-nav-arrow"
            disabled={selectedTeamId === 'all' || availableDateRanges.length === 0 || selectedScheduleId === 'all'}
            onClick={() => {
              const idx = availableDateRanges.findIndex(r => r.id.toString() === selectedScheduleId.toString());
              if (idx <= 0) {
                setSelectedScheduleId('all');
              } else {
                setSelectedScheduleId(availableDateRanges[idx - 1].id.toString());
              }
            }}
          >
            &#8592;
          </button>
          <span className="desktop-date-nav-label">
            {selectedTeamId === 'all' || isLoadingDateRanges
              ? 'Wszystkie daty'
              : selectedScheduleId === 'all'
                ? 'Wszystkie daty'
                : (() => {
                    const range = availableDateRanges.find(r => r.id.toString() === selectedScheduleId.toString());
                    return range ? `${range.startDate} - ${range.endDate}` : 'Wszystkie daty';
                  })()
            }
          </span>
          <button
            className="desktop-date-nav-arrow"
            disabled={selectedTeamId === 'all' || availableDateRanges.length === 0 || (selectedScheduleId !== 'all' && availableDateRanges.findIndex(r => r.id.toString() === selectedScheduleId.toString()) >= availableDateRanges.length - 1)}
            onClick={() => {
              if (selectedScheduleId === 'all') {
                if (availableDateRanges.length > 0) setSelectedScheduleId(availableDateRanges[0].id.toString());
              } else {
                const idx = availableDateRanges.findIndex(r => r.id.toString() === selectedScheduleId.toString());
                if (idx < availableDateRanges.length - 1) setSelectedScheduleId(availableDateRanges[idx + 1].id.toString());
              }
            }}
          >
            &#8594;
          </button>
        </div>
      </div>

      {filteredMatches.length === 0 ? (
        <div className="no-matches">
          {matches.length === 0 ? 'Nie masz jeszcze żadnych zapytań o sparingi.' : 'Brak zapytań dla wybranych filtrów.'}
        </div>
      ) : matchGroups ? (
        // Wyświetl zgrupowane matche (gdy wybrano 'all')
        <div className="matches-grouped">
          {Object.entries(matchGroups).map(([teamName, teamMatches]) => (
            <div key={teamName} className="team-group">
              <h3 className="team-group-header">{teamName}</h3>
              <div className="matches-list">
                {teamMatches.map((match) => {
                  return (
                    <div key={match.id} className="match-item">
                      <div className="match-item-row">
                        {match.opponentLogoUrl && (
                          <img src={match.opponentLogoUrl} alt="" className="match-item-logo" />
                        )}
                        <div className="match-item-content">
                      <div className="match-header">
                        <div className="match-header-row">
                          <div className="match-team-info">
                            <h3 className="match-club-name">
                              {match.teamOpponentName || 'Nieznana drużyna'}
                            </h3>
                            {match.teamOpponentTier && (
                              <span className="match-team-tier">{match.teamOpponentTier}</span>
                            )}
                          </div>
                          <div className="match-right-section">
                            <button
                              className="respond-button"
                              onClick={(e) => openModal(match, e)}
                            >
                              Wiadomości
                            </button>
                          </div>
                        </div>
                        <div className="match-second-row">
                          <div className="match-location">
                            {match.creatorScheduleStartDate && match.creatorScheduleEndDate && (
                              <span className="match-term-info">Termin: {new Date(match.creatorScheduleStartDate).toLocaleDateString('pl-PL')} - {new Date(match.creatorScheduleEndDate).toLocaleDateString('pl-PL')}</span>
                            )}
                            <span>{match.pendingStatus === 'PENDING_WITH_DATE_TIME_CHANGE' || (match.pendingStatus === 'PENDING_WITHOUT_TIME' && match.changeRequesterTeamId && match.changedStartTime) ? 'Obecna data' : 'Data'}: {match.date || 'do ustalenia'}</span>
                            {match.pendingStatus === 'PENDING_WITH_DATE_TIME_CHANGE' && (
                              <span>Obecna godzina: {match.startTime} - {match.endTime}</span>
                            )}
                            {match.pendingStatus === 'PENDING_WITHOUT_TIME' && (
                              <span>Godzina: do ustalenia</span>
                            )}
                            {match.pendingStatus !== 'PENDING_WITH_DATE_TIME_CHANGE' && match.pendingStatus !== 'PENDING_WITHOUT_TIME' && (
                              <span>Godzina: {match.startTime || 'Brak'} - {match.endTime || 'Brak'}</span>
                            )}
                            <span>Boisko: {match.hasPitchAvailable ? (match.location || 'do ustalenia') : 'do ustalenia'}</span>
                          </div>
                        </div>
                        {/* Propozycja zmiany terminu - nowa linia */}
                        {match.pendingStatus === 'PENDING_WITH_DATE_TIME_CHANGE' && (
                          <div className={`time-proposal-row ${match.changeRequesterTeamId === match.teamCreatorId ? 'time-proposal-row--sent' : 'time-proposal-row--received'}`}>
                            <div className="time-proposal-header">
                              {match.changeRequesterTeamId === match.teamCreatorId
                                ? '→ Wysłano propozycję zmiany terminu'
                                : '← Otrzymano propozycję zmiany terminu'}
                            </div>
                            <div className="time-proposal-details">
                              <span className="time-proposal-detail-item">📅 {match.changedDate}</span>
                              <span className="time-proposal-detail-item">⏰ {match.changedStartTime} - {match.changedEndTime}</span>
                              {match.changedLocation && <span className="time-proposal-detail-item">📍 {match.changedLocation}</span>}
                            </div>
                            {match.changeRequesterTeamId !== match.teamCreatorId && (
                              <button
                                className="confirm-time-button"
                                onClick={(e) => handleConfirmTime(match, e)}
                              >
                                Potwierdź termin
                              </button>
                            )}
                          </div>
                        )}
                        {/* Propozycja godziny dla PENDING_WITHOUT_TIME */}
                        {match.pendingStatus === 'PENDING_WITHOUT_TIME' && match.changeRequesterTeamId && match.changedStartTime && match.changedEndTime && (
                          <div className={`time-proposal-row ${match.changeRequesterTeamId === match.teamCreatorId ? 'time-proposal-row--sent' : 'time-proposal-row--received'}`}>
                            <div className="time-proposal-header">
                              {match.changeRequesterTeamId === match.teamCreatorId
                                ? '→ Wysłano propozycję terminu'
                                : '← Otrzymano propozycję terminu'}
                            </div>
                            <div className="time-proposal-details">
                              {match.changedDate && <span className="time-proposal-detail-item">📅 {match.changedDate}</span>}
                              <span className="time-proposal-detail-item">⏰ {match.changedStartTime} - {match.changedEndTime}</span>
                              {match.changedLocation && <span className="time-proposal-detail-item">📍 {match.changedLocation}</span>}
                            </div>
                            {match.changeRequesterTeamId !== match.teamCreatorId && (
                              <button
                                className="confirm-time-button"
                                onClick={(e) => handleConfirmTime(match, e)}
                              >
                                Potwierdź godzinę
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      </div>
                      </div>

                      {/* Wiadomości w kafelku */}
                      {match.messages && match.messages.length > 0 && (
                        <div className="card-messages-section">
                          <div className="card-messages-header">Ostatnia wiadomość:</div>
                          <div className="card-message-text">{match.messages[match.messages.length - 1].message}</div>
                          {match.messages[match.messages.length - 1].createdAt && (
                            <div className="card-message-timestamp">
                              {new Date(match.messages[match.messages.length - 1].createdAt + (match.messages[match.messages.length - 1].createdAt.endsWith('Z') ? '' : 'Z')).toLocaleString('pl-PL')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Wyświetl matche bez grupowania (gdy wybrano konkretną drużynę)
        <div className="matches-list">
          {filteredMatches.map((match) => {
            return (
              <div key={match.id} className="match-item">
                <div className="match-item-row">
                  {match.opponentLogoUrl && (
                    <img src={match.opponentLogoUrl} alt="" className="match-item-logo" />
                  )}
                  <div className="match-item-content">
                <div className="match-header">
                  <div className="match-header-row">
                    <div className="match-team-info">
                      <h3 className="match-club-name">
                        {match.teamOpponentName || 'Nieznana drużyna'}
                      </h3>
                      {match.teamOpponentTier && (
                        <span className="match-team-tier">{match.teamOpponentTier}</span>
                      )}
                    </div>
                    <div className="match-right-section">
                      <button
                        className="respond-button"
                        onClick={(e) => openModal(match, e)}
                      >
                        Wiadomości
                      </button>
                    </div>
                  </div>
                  <div className="match-second-row">
                    <div className="match-location">
                      <span>{match.pendingStatus === 'PENDING_WITH_DATE_TIME_CHANGE' || (match.pendingStatus === 'PENDING_WITHOUT_TIME' && match.changeRequesterTeamId && match.changedStartTime) ? 'Obecna data' : 'Data'}: {match.date || 'do ustalenia'}</span>
                      {match.pendingStatus === 'PENDING_WITH_DATE_TIME_CHANGE' && (
                        <span>Obecna godzina: {match.startTime} - {match.endTime}</span>
                      )}
                      {match.pendingStatus === 'PENDING_WITHOUT_TIME' && (
                        <span>Godzina: do ustalenia</span>
                      )}
                      {match.pendingStatus !== 'PENDING_WITH_DATE_TIME_CHANGE' && match.pendingStatus !== 'PENDING_WITHOUT_TIME' && (
                        <span>Godzina: {match.startTime || 'Brak'} - {match.endTime || 'Brak'}</span>
                      )}
                      <span>Boisko: {match.hasPitchAvailable ? (match.location || 'do ustalenia') : 'do ustalenia'}</span>
                    </div>
                  </div>
                  {/* Propozycja zmiany terminu - nowa linia */}
                  {match.pendingStatus === 'PENDING_WITH_DATE_TIME_CHANGE' && (
                    <div className={`time-proposal-row ${match.changeRequesterTeamId === match.teamCreatorId ? 'time-proposal-row--sent' : 'time-proposal-row--received'}`}>
                      <div className="time-proposal-header">
                        {match.changeRequesterTeamId === match.teamCreatorId
                          ? '→ Wysłano propozycję zmiany terminu'
                          : '← Otrzymano propozycję zmiany terminu'}
                      </div>
                      <div className="time-proposal-details">
                        <span className="time-proposal-detail-item">📅 {match.changedDate}</span>
                        <span className="time-proposal-detail-item">⏰ {match.changedStartTime} - {match.changedEndTime}</span>
                        {match.changedLocation && <span className="time-proposal-detail-item">📍 {match.changedLocation}</span>}
                      </div>
                      {match.changeRequesterTeamId !== match.teamCreatorId && (
                        <button
                          className="confirm-time-button"
                          onClick={(e) => handleConfirmTime(match, e)}
                        >
                          Potwierdź termin
                        </button>
                      )}
                    </div>
                  )}
                  {/* Propozycja godziny dla PENDING_WITHOUT_TIME */}
                  {match.pendingStatus === 'PENDING_WITHOUT_TIME' && match.changeRequesterTeamId && match.changedStartTime && match.changedEndTime && (
                    <div className={`time-proposal-row ${match.changeRequesterTeamId === match.teamCreatorId ? 'time-proposal-row--sent' : 'time-proposal-row--received'}`}>
                      <div className="time-proposal-header">
                        {match.changeRequesterTeamId === match.teamCreatorId
                          ? '→ Wysłano propozycję terminu'
                          : '← Otrzymano propozycję terminu'}
                      </div>
                      <div className="time-proposal-details">
                        {match.changedDate && <span className="time-proposal-detail-item">📅 {match.changedDate}</span>}
                        <span className="time-proposal-detail-item">⏰ {match.changedStartTime} - {match.changedEndTime}</span>
                        {match.changedLocation && <span className="time-proposal-detail-item">📍 {match.changedLocation}</span>}
                      </div>
                      {match.changeRequesterTeamId !== match.teamCreatorId && (
                        <button
                          className="confirm-time-button"
                          onClick={(e) => handleConfirmTime(match, e)}
                        >
                          Potwierdź godzinę
                        </button>
                      )}
                    </div>
                  )}
                </div>

                  </div>
                </div>

                {/* Wiadomości w kafelku */}
                {match.messages && match.messages.length > 0 && (
                  <div className="card-messages-section">
                    <div className="card-messages-header">Ostatnia wiadomość:</div>
                    <div className="card-message-text">{match.messages[match.messages.length - 1].message}</div>
                    {match.messages[match.messages.length - 1].createdAt && (
                      <div className="card-message-timestamp">
                        {new Date(match.messages[match.messages.length - 1].createdAt + (match.messages[match.messages.length - 1].createdAt.endsWith('Z') ? '' : 'Z')).toLocaleString('pl-PL')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Kontrolki paginacji */}
      {totalPages > 1 && (
        <div className="pagination-controls">
          <button
            className="pagination-button"
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
          >
            Poprzednia
          </button>
          <span className="pagination-info">
            Strona {page + 1} z {totalPages} (Łącznie: {totalElements})
          </span>
          <button
            className="pagination-button"
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1}
          >
            Następna
          </button>
        </div>
      )}

      {/* Modal */}
      {modalOpen && selectedMatch && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Podgląd zapytania</h3>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>
            <div className="modal-body">
              {/* Szczegóły zapytania o sparing */}
              <div className="match-details-section">
                <h4>{selectedMatch.pendingStatus === 'PENDING_WITH_DATE_TIME_CHANGE' ? 'Zapytanie o zmianę terminu' : 'Zapytanie o sparing'}</h4>

                <div className="details-teams-row">
                  <div className="details-team-column">
                    {selectedMatch.creatorLogoUrl && (
                      <div className="details-team-logo">
                        <img src={selectedMatch.creatorLogoUrl} alt="Logo" />
                      </div>
                    )}
                    <p className="details-team-name">{selectedMatch.teamCreatorName || 'Brak danych'}</p>
                    {selectedMatch.teamCreatorTier && (
                      <p className="details-team-tier">{selectedMatch.teamCreatorTier}</p>
                    )}
                  </div>

                  <div className="details-team-column">
                    {selectedMatch.opponentLogoUrl && (
                      <div className="details-team-logo">
                        <img src={selectedMatch.opponentLogoUrl} alt="Logo" />
                      </div>
                    )}
                    <p className="details-team-name">{selectedMatch.teamOpponentName || 'Brak danych'}</p>
                    {selectedMatch.teamOpponentTier && (
                      <p className="details-team-tier">{selectedMatch.teamOpponentTier}</p>
                    )}
                  </div>
                </div>

                <h5 className="details-team-title" style={{ marginTop: '1rem' }}>
                  {selectedMatch.pendingStatus === 'PENDING_WITHOUT_TIME' ? 'Proponowany termin' : 'Termin'}
                </h5>
                <div className="details-grid">
                  {selectedMatch.opponentScheduleStartDate && selectedMatch.opponentScheduleEndDate && (
                    <div className="detail-item">
                      <span className="detail-label">Propozycja na termin:</span>
                      <span className="detail-value">
                        {new Date(selectedMatch.opponentScheduleStartDate).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        {' - '}
                        {new Date(selectedMatch.opponentScheduleEndDate).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </span>
                    </div>
                  )}
                  {selectedMatch.pendingStatus === 'PENDING_WITHOUT_TIME' && selectedMatch.changedStartTime && selectedMatch.changedEndTime ? (
                    <>
                      {selectedMatch.changedDate && (
                        <div className="detail-item">
                          <span className="detail-label">Data:</span>
                          <span className="detail-value">{selectedMatch.changedDate}</span>
                        </div>
                      )}
                      <div className="detail-item">
                        <span className="detail-label">Godzina rozpoczęcia:</span>
                        <span className="detail-value">{selectedMatch.changedStartTime}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Godzina zakończenia:</span>
                        <span className="detail-value">{selectedMatch.changedEndTime}</span>
                      </div>
                      {selectedMatch.changedLocation && (
                        <div className="detail-item">
                          <span className="detail-label">Boisko:</span>
                          <span className="detail-value">{selectedMatch.changedLocation}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="detail-item">
                        <span className="detail-label">Data:</span>
                        <span className="detail-value">{selectedMatch.date || 'do ustalenia'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Godzina rozpoczęcia:</span>
                        <span className="detail-value">{selectedMatch.startTime || 'do ustalenia'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Godzina zakończenia:</span>
                        <span className="detail-value">{selectedMatch.endTime || 'do ustalenia'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Boisko:</span>
                        <span className="detail-value">
                          {selectedMatch.hasPitchAvailable
                            ? (selectedMatch.location || 'do ustalenia')
                            : 'do ustalenia'}
                        </span>
                      </div>
                    </>
                  )}
                  {selectedMatch.pendingStatus !== 'PENDING_WITH_DATE_TIME_CHANGE' && (
                    <div className="detail-item">
                      <span className="detail-label">Status:</span>
                      <span className={`status-badge ${getStatusClass(selectedMatch.status)}`}>
                        {getStatusLabel(selectedMatch.status)}
                      </span>
                    </div>
                  )}
                </div>

                {selectedMatch.pendingStatus === 'PENDING_WITH_DATE_TIME_CHANGE' && selectedMatch.changedStartTime && selectedMatch.changedEndTime && (
                  <div className="proposed-time-section">
                    <h4>Propozycja zmiany terminu</h4>
                    <div className="details-grid">
                      {selectedMatch.changedDate && (
                        <div className="detail-item">
                          <span className="detail-label">Proponowana data:</span>
                          <span className="detail-value">{selectedMatch.changedDate}</span>
                        </div>
                      )}
                      <div className="detail-item">
                        <span className="detail-label">Proponowana godzina rozpoczęcia:</span>
                        <span className="detail-value">{selectedMatch.changedStartTime}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Proponowana godzina zakończenia:</span>
                        <span className="detail-value">{selectedMatch.changedEndTime}</span>
                      </div>
                      {selectedMatch.changedLocation && (
                        <div className="detail-item">
                          <span className="detail-label">Proponowana lokalizacja:</span>
                          <span className="detail-value">{selectedMatch.changedLocation}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Sekcja odpowiedzi */}
              <div className="reply-section">
                <h4>Odpowiedz</h4>
                <div className="reply-form">
                  <textarea
                    className="reply-textarea"
                    placeholder="Wpisz swoją wiadomość..."
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    rows={3}
                    maxLength={800}
                  />
                  <button
                    className="reply-send-button"
                    onClick={handleSendReply}
                    disabled={sendingReply || !replyMessage.trim()}
                  >
                    {sendingReply ? 'Wysyłanie...' : 'Wyślij'}
                  </button>
                </div>
              </div>

              {/* Wiadomości */}
              <div className="messages-section">
                <h4>Wiadomości</h4>
                {selectedMatch.messages && selectedMatch.messages.length > 0 ? (
                  <div className="messages-list">
                    {selectedMatch.messages.map((message) => {
                      // W SportsClubMatches zalogowany użytkownik jest creatorem
                      const isOwnMessage = message.senderId === selectedMatch.teamCreatorId;
                      const senderName = isOwnMessage
                        ? selectedMatch.sportsClubCreatorName
                        : selectedMatch.sportsClubOpponentName;
                      return (
                        <div
                          key={message.id}
                          className={`message-item ${isOwnMessage ? 'message-own' : 'message-other'}`}
                        >
                          <div className="message-content">
                            <div className="message-sender">{senderName}</div>
                            <div className="message-text">{message.message}</div>
                            {message.createdAt && (
                              <div className="message-timestamp">
                                {new Date(message.createdAt + (message.createdAt.endsWith('Z') ? '' : 'Z')).toLocaleString('pl-PL')}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="no-messages">Brak wiadomości</div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="modal-action-button close-button"
                onClick={closeModal}
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default SportsClubMatches;
